import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import React, { useState, useEffect, useCallback } from 'react';
import Select from '@mui/material/Select';
import PropTypes from 'prop-types';
import FiltersDrawer from '../../../FiltersDrawer';
import { useUserApi } from '../../../../apis';
import { parseApiResponseData } from '../../../../apis/Utilities/parseApiResponseData';
import useIsMountedRef from '../../../../hooks/useIsMountedRef';
import {
	renderData,
	sortCategories,
	sortPlants,
} from '../../../../utils/products';

function ProductFilters({ value, onFilter }) {
	const { t } = useTranslation('products', 'common');
	const isMountedRef = useIsMountedRef();
	const [isLoading, setIsLoading] = useState(false);
	const [{ categoryOptions = [], plantOptions = [] }, setFilterOptions] =
		useState({});
	const categories = categoryOptions.slice(1);
	const { getProductFilters } = useUserApi();

	const fetchProductFilter = useCallback(async () => {
		setIsLoading(true);

		const response = await getProductFilters();

		if (response?.isAborted) {
			setIsLoading(false);
			return;
		}

		if (response?.isError) {
			console.error(response.error || 'failed to load product filters');
			setIsLoading(false);
			return;
		}

		const parsed = parseApiResponseData(response, {});

		const filters = parsed?.filters ?? [];

		const options = {
			category:
				filters.find((item) => item?.filterName === 'Product_Category')
					?.filterValues ?? [],
			plant:
				filters.find((item) => item?.filterName === 'Plant_Code')
					?.filterValues ?? [],
		};

		if (isMountedRef.current) {
			setFilterOptions({
				categoryOptions: sortCategories(options.category),
				plantOptions: sortPlants(options.plant),
			});
		}

		setIsLoading(false);
	}, [isMountedRef, getProductFilters]);

	useEffect(() => {
		fetchProductFilter();
	}, [fetchProductFilter]);

	const [filters, setFilters] = useState(value || {});
	return (
		<FiltersDrawer
			onApply={() => onFilter(filters)}
			onReset={() => {
				onFilter({});
				setFilters({});
			}}
		>
			{isLoading ? (
				<LinearProgress />
			) : (
				({ Section, InputContainer }) => (
					<>
						<Section isFirst header={t('category')}>
							<InputContainer>
								<FormControl component="fieldset">
									<RadioGroup
										aria-label="category"
										name="category"
										value={filters.category || ''}
										onChange={({ target }) =>
											setFilters({ ...filters, category: target.value })
										}
									>
										<FormControlLabel
											value="ALL"
											control={<Radio />}
											label={t('categories.0.name')}
										/>
										{categories.map((row) => (
											<Box key={row.name} display="flex" alignItems="center">
												<Box flexGrow={1}>
													<FormControlLabel
														value={row.name}
														control={<Radio />}
														label={renderData(row.name)}
													/>
												</Box>
											</Box>
										))}
									</RadioGroup>
								</FormControl>
							</InputContainer>
						</Section>
						<Section header={t('location')}>
							<InputContainer>
								<FormControl variant="outlined" fullWidth>
									<InputLabel shrink>{t('site')}</InputLabel>
									<Select
										displayEmpty
										value={filters.plant ?? ''}
										onChange={({ target }) =>
											setFilters({ ...filters, plant: target.value })
										}
										input={<OutlinedInput notched label={t('site')} />}
									>
										<MenuItem value="" key={999999999}>
											{t('all')}
										</MenuItem>
										{plantOptions.map((row) => (
											<MenuItem
												key={row.value}
												value={row.value}
												data-testid="products_filters_site_item"
												data-testvalue={row.value}
											>
												{renderData(row.name)}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</InputContainer>
						</Section>
					</>
				)
			)}
		</FiltersDrawer>
	);
}

ProductFilters.propTypes = {
	onFilter: PropTypes.func.isRequired,
	value: PropTypes.any,
};

export default ProductFilters;
